import type { OrgSubUnit, SubUnitType } from '@appTypes/esb';
import { denTypes } from '@shared/constants';

import {
  type RosterMemberView,
  type SubUnitRef,
  formatSubUnitLabel,
  subUnitKey,
} from './rosterMemberView';

/** Group key and Sub-Unit filter value of "No den/patrol" */
export const NO_SUB_UNIT_KEY = 'none';

export interface RosterGroup {
  key: string;
  /** Empty for "No den/patrol": the view translates it */
  label: string;
  ref?: SubUnitRef;
}

const NO_SUB_UNIT_GROUP: RosterGroup = { key: NO_SUB_UNIT_KEY, label: '' };

const DEN_LEVEL_ORDER: ReadonlyMap<string, number> = new Map(
  denTypes.map(({ name }, index) => [name, index]),
);

/** Unknown levels go last */
function denLevelIndex(denType: string | undefined): number {
  return DEN_LEVEL_ORDER.get(denType?.toLowerCase() ?? '') ?? denTypes.length;
}

function compareByLabel(a: SubUnitRef, b: SubUnitRef): number {
  return (
    a.label.localeCompare(b.label, undefined, { numeric: true }) || a.id - b.id
  );
}

function compareDens(a: SubUnitRef, b: SubUnitRef): number {
  return (
    denLevelIndex(a.denType) - denLevelIndex(b.denType) || compareByLabel(a, b)
  );
}

/**
 * The members' dens or patrols plus `orgSubUnits`, so empty ones show too. The
 * roster's label wins. Dens sort by level, then label; labels sort numerically.
 */
export function collectSubUnitRefs(
  subUnitType: SubUnitType | undefined,
  members: readonly Pick<RosterMemberView, 'subUnits'>[],
  orgSubUnits?: readonly Pick<
    OrgSubUnit,
    'subUnitId' | 'subUnitName' | 'denType'
  >[],
): SubUnitRef[] {
  const byKey = new Map<string, SubUnitRef>();
  if (subUnitType) {
    orgSubUnits?.forEach(subUnit => {
      const label = formatSubUnitLabel(subUnit, subUnitType);
      if (!label) return;
      const ref: SubUnitRef = {
        id: subUnit.subUnitId,
        type: subUnitType,
        label,
      };
      const denType = subUnitType === 'den' && subUnit.denType?.trim();
      if (denType) ref.denType = denType;
      byKey.set(subUnitKey(ref), ref);
    });
  }
  members.forEach(member =>
    member.subUnits.forEach(ref => {
      if (subUnitType && ref.type !== subUnitType) return;
      const key = subUnitKey(ref);
      byKey.set(key, { ...byKey.get(key), ...ref });
    }),
  );
  return Array.from(byKey.values()).sort(
    subUnitType === 'den' ? compareDens : compareByLabel,
  );
}

/** One group per ref, in order, then "No den/patrol" */
export function buildGroups(subUnitRefs: readonly SubUnitRef[]): RosterGroup[] {
  return [
    ...subUnitRefs.map(ref => ({
      key: subUnitKey(ref),
      label: ref.label,
      ref,
    })),
    NO_SUB_UNIT_GROUP,
  ];
}

/** Values are group keys; `noneText` adds a last "No den/patrol" option. */
export function buildSubUnitFilterOptions(
  subUnitRefs: readonly SubUnitRef[],
  noneText?: string,
): { text: string; value: string }[] {
  const options = subUnitRefs.map(ref => ({
    text: ref.label,
    value: subUnitKey(ref),
  }));
  return noneText === undefined
    ? options
    : [...options, { text: noneText, value: NO_SUB_UNIT_KEY }];
}
